import { useEffect, useRef } from 'react'
import { connectGlobalEventStream } from '../lib/api/sse'
import { dispatchChatEvent } from '../../features/chat/hooks/useChat'
import { dispatchWorkflowEvent } from '../../features/orchestration/store/slices/streamingSlice'
import { dispatchEnrichmentEvent } from '../../features/orchestration/store/slices/enrichmentSlice'
import { dispatchOrchestratorEvent } from '../../features/orchestration/hooks/useOrchestratorStream'
import type { GlobalEventEnvelope } from '../lib/api/sse'

/**
 * Mounts a single persistent SSE connection to GET /api/events/stream.
 * All events are routed to the appropriate domain dispatcher based on their
 * `source` field. The sequence number is persisted to localStorage so the
 * client can resume from where it left off on reconnect.
 *
 * @param userKey  User identifier (email) used to namespace the seq key.
 *                 Pass null/undefined to skip connecting (not yet authenticated).
 */
export function useGlobalEventStream(userKey: string | null | undefined) {
  const clientRef = useRef<ReturnType<typeof connectGlobalEventStream> | null>(null)
  // Track the next expected seq in a ref (not localStorage) so it resets to 0
  // on every page load. This prevents stale seq values from silently filtering
  // out all events after a backend restart.
  const sinceRef = useRef<number>(0)

  useEffect(() => {
    if (!userKey) return

    sinceRef.current = 0

    function dispatch(envelope: GlobalEventEnvelope) {
      sinceRef.current = envelope.seq + 1

      switch (envelope.source) {
        case 'chat':
          dispatchChatEvent(envelope)
          break
        case 'workflow':
          dispatchWorkflowEvent(envelope)
          break
        case 'enrichment':
          dispatchEnrichmentEvent(envelope)
          break
        case 'orchestrator':
          dispatchOrchestratorEvent(envelope)
          break
        case 'scan':
          // Scan events: will be handled when dispatchScanEvent is implemented
          break
        default:
          break
      }
    }

    const client = connectGlobalEventStream(
      () => sinceRef.current,
      dispatch,
    )
    clientRef.current = client

    return () => {
      client.disconnect()
      clientRef.current = null
    }
  }, [userKey])
}
